import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "../lib/api";
import { logger } from "../lib/logger";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { ArtworkUploadForm } from "../components/dashboard/ArtworkUploadForm";
import { ArtistArtworkGrid } from "../components/dashboard/ArtistArtworkGrid";
import { SalesList } from "../components/dashboard/SalesList";
import { AcquisitionsList } from "../components/dashboard/AcquisitionsList";
import { AnalyticsCards } from "../components/dashboard/AnalyticsCards";
import { ProfileEditor } from "../components/dashboard/ProfileEditor";
import { ContributorArticlesPanel, ContributorEventsPanel } from "../components/dashboard/ContributorPanels";
import { ArtistCenterCard } from "../components/dashboard/ArtistCenterCard";
import { CenterDashboard } from "../components/dashboard/CenterDashboard";
import { ApplyTimeline } from "../components/ApplyTimeline";

export default function Dashboard() {
  const { user, loading, updateUser } = useAuth();
  const navigate = useNavigate();
  const [myArtworks, setMyArtworks] = useState([]);
  const [orders, setOrders] = useState([]);
  const [sales, setSales] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [payingOrderId, setPayingOrderId] = useState(null);
  const [activeTab, setActiveTab] = useState("overview"); // overview | profile

  useEffect(() => {
    if (!loading && !user) navigate("/login");
  }, [user, loading, navigate]);

  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        if (user.role === "artist") {
          const [a, s] = await Promise.all([
            api.get("/artworks", { params: { artist_id: user.id } }),
            api.get("/sales/mine"),
          ]);
          setMyArtworks(a.data);
          setSales(s.data);
        }
        const o = await api.get("/orders/mine");
        setOrders(o.data);
      } catch (err) {
        logger.error("Dashboard data load failed:", err);
      }
    })();
  }, [user]);

  const handlePayOrder = async (orderId) => {
    setPayingOrderId(orderId);
    try {
      const { data } = await api.post(`/orders/${orderId}/checkout`);
      window.location.href = data.url;
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not start checkout");
      setPayingOrderId(null);
    }
  };

  const handleArtworkCreated = (data) => {
    setMyArtworks((prev) => [data, ...prev]);
    setShowForm(false);
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/artworks/${id}`);
      setMyArtworks((prev) => prev.filter((a) => a.id !== id));
      toast.success("Removed from gallery");
    } catch (_e) {
      toast.error("Could not delete");
    }
  };

  const handleAuction = async (art) => {
    const startStr = window.prompt(
      `Starting price for "${art.title}" (USD)?\nSuggested: $${Math.round(art.price * 0.6)}`,
      String(Math.round(art.price * 0.6))
    );
    if (!startStr) return;
    const starting = parseFloat(startStr);
    if (!starting || starting <= 0) {
      toast.error("Invalid starting price");
      return;
    }
    const hoursStr = window.prompt("Duration in hours (default 48):", "48");
    if (!hoursStr) return;
    const hours = parseInt(hoursStr) || 48;
    try {
      const { data } = await api.post("/auctions", {
        artwork_id: art.id,
        starting_price: starting,
        duration_hours: hours,
      });
      toast.success("Auction is live");
      window.location.href = `/auction/${data.id}`;
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not start auction");
    }
  };

  if (loading || !user) return <div className="p-24 label-eyebrow text-[#555]">Loading…</div>;

  const isPending = user.approval_status === "pending";
  const isRejected = user.approval_status === "rejected";
  const isApproved = !isPending && !isRejected;

  return (
    <div data-testid="dashboard-page" className="px-6 md:px-12 lg:px-24 py-16">
      <p className="label-eyebrow text-[#555]">— {user.role === "artist" ? "Studio" : "Account"}</p>
      <h1 className="font-serif-display text-5xl md:text-6xl tracking-tighter leading-none font-light mt-4">
        Hello, {user.name.split(" ")[0]}
      </h1>
      <p className="text-[#555] mt-3 font-mono-aam text-xs">
        {user.role.replace("_", " ").toUpperCase()} · {user.email}
      </p>

      {isPending && (
        <div
          data-testid="pending-approval-banner"
          className="mt-10 border-2 border-[#F4C430] bg-[#F4C430]/15 px-6 py-5"
        >
          <p className="label-eyebrow text-[#1E3A8A]">— Pending review</p>
          <p className="font-serif-display text-2xl mt-2 text-[#111]">
            Your account is awaiting admin approval
          </p>
          <p className="text-sm text-[#333] mt-2 max-w-xl leading-relaxed">
            We review every new account before activating purchases, bidding, and artist uploads.
            You'll receive an email at <span className="font-mono-aam text-[#1E3A8A]">{user.email}</span>{" "}
            once your account is approved — usually within 24 hours.
          </p>
          <ApplyTimeline stage="verified" />
        </div>
      )}

      {isRejected && (
        <div
          data-testid="rejected-approval-banner"
          className="mt-10 border-2 border-[#D32F2F] bg-[#D32F2F]/10 px-6 py-5"
        >
          <p className="label-eyebrow text-[#D32F2F]">— Account not approved</p>
          <p className="font-serif-display text-2xl mt-2 text-[#111]">
            Your application was not approved
          </p>
          <p className="text-sm text-[#333] mt-2 max-w-xl">
            If you believe this is a mistake, please contact{" "}
            <a href="mailto:sales@africaartmasters.com" className="text-[#1E3A8A] underline">
              sales@africaartmasters.com
            </a>
            .
          </p>
        </div>
      )}

      {user.role === "artist" && isApproved && (
        <>
          <div className="mt-12 flex items-center gap-2 border-b border-[#E5E0D8]" data-testid="dashboard-tabs">
            <button
              data-testid="tab-overview"
              onClick={() => setActiveTab("overview")}
              className={`label-eyebrow px-4 py-3 border-b-2 transition-colors ${
                activeTab === "overview"
                  ? "border-[#1E3A8A] text-[#1E3A8A]"
                  : "border-transparent text-[#555] hover:text-[#111]"
              }`}
            >
              Overview
            </button>
            <button
              data-testid="tab-profile"
              onClick={() => setActiveTab("profile")}
              className={`label-eyebrow px-4 py-3 border-b-2 transition-colors ${
                activeTab === "profile"
                  ? "border-[#1E3A8A] text-[#1E3A8A]"
                  : "border-transparent text-[#555] hover:text-[#111]"
              }`}
            >
              Profile
            </button>
          </div>

          {activeTab === "overview" && (
            <>
              <AnalyticsCards />
              <ArtistCenterCard />

              <div className="mt-16 flex items-end justify-between border-b border-[#E5E0D8] pb-6">
                <div>
                  <p className="label-eyebrow text-[#555]">— Your Works</p>
                  <h2 className="font-serif-display text-3xl mt-2 font-light">
                    {myArtworks.length} on view · {sales.length} sold
                  </h2>
                </div>
                <Button
                  data-testid="add-artwork-btn"
                  onClick={() => setShowForm(!showForm)}
                  className="rounded-none bg-[#1E3A8A] hover:bg-[#162a66] text-white px-6 py-3 h-auto label-eyebrow"
                >
                  {showForm ? "Cancel" : "+ Add Artwork"}
                </Button>
              </div>

              {showForm && (
                <ArtworkUploadForm
                  user={user}
                  onCreated={handleArtworkCreated}
                  onCancel={() => setShowForm(false)}
                />
              )}

              <ArtistArtworkGrid
                artworks={myArtworks}
                onDelete={handleDelete}
                onAuction={handleAuction}
                onUpdated={(updated) =>
                  setMyArtworks((prev) => prev.map((a) => (a.id === updated.id ? updated : a)))
                }
              />

              <SalesList sales={sales} />
            </>
          )}

          {activeTab === "profile" && (
            <ProfileEditor user={user} onUpdated={updateUser} />
          )}
        </>
      )}

      {user.role === "art_center" && isApproved && (
        <CenterDashboard user={user} onUserUpdated={updateUser} />
      )}

      {user.role !== "artist" && user.role !== "writer" && user.role !== "art_center" && isApproved && (
        <>
          <div className="mt-12 flex items-center gap-2 border-b border-[#E5E0D8]" data-testid="dashboard-tabs-buyer">
            <button
              data-testid="tab-overview"
              onClick={() => setActiveTab("overview")}
              className={`label-eyebrow px-4 py-3 border-b-2 transition-colors ${
                activeTab === "overview"
                  ? "border-[#1E3A8A] text-[#1E3A8A]"
                  : "border-transparent text-[#555] hover:text-[#111]"
              }`}
            >
              Overview
            </button>
            <button
              data-testid="tab-profile"
              onClick={() => setActiveTab("profile")}
              className={`label-eyebrow px-4 py-3 border-b-2 transition-colors ${
                activeTab === "profile"
                  ? "border-[#1E3A8A] text-[#1E3A8A]"
                  : "border-transparent text-[#555] hover:text-[#111]"
              }`}
            >
              Profile
            </button>
          </div>
          {activeTab === "overview" && <AnalyticsCards />}
          {activeTab === "profile" && <ProfileEditor user={user} onUpdated={updateUser} />}
        </>
      )}

      {/* Journalist / Writer dashboard — articles + events */}
      {["writer", "journalist", "storyteller"].includes(user.role) && isApproved && (
        <div data-testid="writer-dashboard" className="mt-12">
          <p className="label-eyebrow text-[#1E3A8A]">— African Stories · Submissions Portal</p>
          <h2 className="font-serif-display text-3xl mt-2 font-light">
            Welcome, {user.name?.split(" ")[0]}
          </h2>
          <p className="text-sm text-[#555] mt-2 max-w-2xl">
            Submit articles, photo essays, and video reports to African Stories.
            Drafts are reviewed by an editor before publication. You can attach
            up to 20 photos and one video URL per article.
          </p>
          <ContributorArticlesPanel />
          <ContributorEventsPanel />
        </div>
      )}

      {/* Artists also get events but no article panel (events only) — they can still contribute editorial via /admin invite. */}
      {user.role === "artist" && isApproved && activeTab === "overview" && (
        <ContributorEventsPanel />
      )}

      {isApproved && activeTab === "overview" && (
        <AcquisitionsList orders={orders} onPay={handlePayOrder} payingOrderId={payingOrderId} />
      )}
    </div>
  );
}
